import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import Pill from './userDetails/Pill';
import BillingInformationSection from './userDetails/BillingInformationSection';
import AchRiskProfileSection from './userDetails/AchRiskProfileSection';
import ShipmentSummariesSection from './userDetails/ShipmentSummariesSection';
import WarehousesSection from './userDetails/WarehousesSection';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  jumpLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },

  jumpLinksLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: '24px',
    textTransform: 'uppercase',
    color: theme.palette.secondary.contrastText,
    marginRight: theme.spacing(1),
  },

  jumpLink: {
    border: 0,
    background: 'transparent',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
  },

  twoColumns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
    gap: '24px',
    alignItems: 'start',
  },
}));

const JUMP_LINKS = [
  { id: 'user-details-billing', label: 'Billing' },
  { id: 'user-details-ach', label: 'ACH risk profile' },
  { id: 'user-details-shipments', label: 'Shipments' },
  { id: 'user-details-warehouses', label: 'Warehouses' },
];

const UserDetailsTab = ({ user }) => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const notify = useNotify();

  const { id: userId, currency, billingUserId } = user;

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [achData, setAchData] = useState<any>(null);

  const getUserDetails = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await dataProvider.getNest('userDetails', {
        userId,
        billingUserId,
        currency,
      });
      setUserDetails(data);
    } catch (error: any) {
      notify(error?.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, userId, billingUserId, currency, notify]);

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  useEffect(() => {
    const getAchData = async () => {
      try {
        const { data } = await dataProvider.getOne('userRiskProfile', {
          id: billingUserId,
        });

        setAchData(data);
      } catch (error: any) {
        notify(error?.message ?? error, 'error');
      }
    };

    getAchData();
  }, [billingUserId, dataProvider, notify]);

  const handleJump = (id: string) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box className={classes.root}>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : userDetails ? (
        <>
          <div className={classes.jumpLinks}>
            <span className={classes.jumpLinksLabel}>On this page</span>
            {JUMP_LINKS.map((link, index) => (
              <button
                key={link.id}
                type="button"
                className={classes.jumpLink}
                onClick={handleJump(link.id)}>
                <Pill variant={index === 0 ? 'primaryTint' : 'neutral'}>{link.label}</Pill>
              </button>
            ))}
          </div>

          <BillingInformationSection
            userId={userId}
            billingUserSettings={userDetails.billingUserSettings}
          />

          <div className={classes.twoColumns}>
            <AchRiskProfileSection achData={achData} />
            <ShipmentSummariesSection
              summaries={userDetails.shipmentSummaries}
              currency={currency}
            />
          </div>

          <WarehousesSection warehouses={userDetails.warehouses} />
        </>
      ) : (
        <Typography color="textSecondary">No user details available</Typography>
      )}
    </Box>
  );
};

export default UserDetailsTab;
